import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useHospitalBranding } from '@/contexts/HospitalBrandingContext';
import { apiRequest } from '@/config/api';
import { 
  Stethoscope, 
  Users, 
  Calendar, 
  FileText, 
  Shield, 
  Clock,
  Star,
  ArrowRight,
  Smile,
  MapPin,
  Phone,
  CheckCircle2,
  Mail,
  Youtube,
  Facebook,
  Instagram,
  AlertCircle
} from 'lucide-react';
import WhatsAppButton from '@/components/ui/WhatsAppButton';

const HospitalLandingPage = () => {
  const { hospitalSlug } = useParams();
  const { branding: contextBranding, getHospitalName: getContextHospitalName } = useHospitalBranding();
  const [hospitalBranding, setHospitalBranding] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [planType, setPlanType] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Use context branding if available (when logged in), otherwise use fetched branding
  const branding = contextBranding || hospitalBranding;
  const hospitalName = branding?.name || getContextHospitalName() || 'Dental Practice';

  useEffect(() => {
    // If no context branding (not logged in), fetch hospital data by slug
    if (!contextBranding && hospitalSlug) {
      const fetchHospitalData = async () => {
        setLoading(true);
        try {
          console.log('HospitalLandingPage: Fetching hospital data for slug:', hospitalSlug);
          const data = await apiRequest(`get_hospital_by_slug.php?slug=${hospitalSlug}`);
          console.log('HospitalLandingPage: API response:', data);
          if (data.success && data.hospital) {
            setHospitalBranding({
              name: data.hospital.name,
              logoUrl: data.hospital.logo_url,
              address: data.hospital.address,
              phone: data.hospital.phone,
              email: data.hospital.email,
              slug: data.hospital.slug
            });
            
            // Set plan type
            setPlanType(data.hospital.plan_type || 'TRIAL');
            
            // Set trial info if plan type is TRIAL
            if (data.hospital.plan_type === 'TRIAL' && data.hospital.trial_end_at && data.hospital.is_trial_active) {
              const trialEnd = new Date(data.hospital.trial_end_at);
              const now = new Date();
              const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
              
              setTrialInfo({
                endDate: trialEnd,
                daysLeft: daysLeft > 0 ? daysLeft : 0,
                isActive: data.hospital.is_trial_active
              });
            }
            
            // Set subscription info if plan type is SILVER or GOLD
            if ((data.hospital.plan_type === 'SILVER' || data.hospital.plan_type === 'GOLD') && data.hospital.subscription_end_at) {
              const renewalDate = new Date(data.hospital.subscription_end_at);
              const now = new Date();
              const daysUntilRenewal = Math.ceil((renewalDate - now) / (1000 * 60 * 60 * 24));
              
              // Only show if within last 7 days before renewal
              if (daysUntilRenewal >= 0 && daysUntilRenewal <= 7) {
                setSubscriptionInfo({
                  renewalDate: renewalDate,
                  daysUntilRenewal: daysUntilRenewal,
                  planType: data.hospital.plan_type,
                  currentPlan: data.hospital.current_plan
                });
              }
            }
          } else {
            console.error('HospitalLandingPage: Hospital not found or not approved');
          }
        } catch (error) {
          console.error('HospitalLandingPage: Failed to load hospital data:', error);
        } finally {
          setLoading(false);
        }
      };
      
      fetchHospitalData();
    } else {
      setLoading(false);
    }
  }, [hospitalSlug, contextBranding]);

  // Debug logging
  useEffect(() => {
    console.log('HospitalLandingPage rendered with slug:', hospitalSlug);
    console.log('Context branding:', contextBranding);
    console.log('Hospital branding:', hospitalBranding);
    console.log('Loading:', loading);
  }, [hospitalSlug, contextBranding, hospitalBranding, loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center space-x-2"
          >
            <Stethoscope className="h-8 w-8 text-blue-400 animate-pulse" />
            <span className="text-xl font-bold gradient-text">Loading...</span>
          </motion.div>
        </div>
      </div>
    );
  }
  
  // If no hospital data and not loading, show error or fallback
  if (!branding && !loading && hospitalSlug) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-md mx-auto p-8"
          >
            <Stethoscope className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Hospital Not Found</h1>
            <p className="text-gray-400 mb-4">
              The hospital "{hospitalSlug}" could not be found or is not approved.
            </p>
            <Link to="/">
              <Button className="medical-gradient text-white">
                Go to Home
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  const features = [
    {
      icon: FileText,
      title: 'Seamless Clinical Documentation',
      description: 'Digital dental charts and automated clinical notes. Treatment histories, progress tracking, and diagnostics in one place. Easy access to records for patient visits and follow-ups. Designed for dental workflows to reduce paperwork and speed up care.'
    },
    {
      icon: Calendar,
      title: 'Smart Dental Scheduling',
      description: 'Intelligent appointment booking based on dentist availability. Online booking and automated SMS/email reminders. Chair-based scheduling to optimize clinic flow. Minimize no-shows and keep your schedule synchronized.'
    },
    {
      icon: Users,
      title: 'Role-Based Portals',
      description: 'Separate secure access for dentists, hygienists, admins, and patients. Patients can view records, appointments, and communications. Staff can manage clinical tasks and administrative duties efficiently.'
    },
    {
      icon: FileText,
      title: 'Digital Dental Forms & Imaging',
      description: 'Online intake forms, consent signatures, and health questionnaires. Imaging integrations — X-rays, intraoral photos, and charts in one record. Make the first visit, treatment planning, and documentation smoother.'
    },
    {
      icon: Stethoscope,
      title: 'Reporting & Analytics',
      description: 'Monitor clinic performance and patient outcomes. Insights into appointment trends, treatment plans, and resource use. Improve practice management decisions with real data.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{hospitalName} — Advanced Dental Practice with Integrated EHR Solution</title>
        <meta name="description" content={`Multi-specialty dental clinic with state-of-the-art Dental EHR & Practice Management System.`} />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Navigation */}
        <nav className="fixed top-0 w-full z-50 glass-effect border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-3 lg:py-0 lg:h-20">
              {/* Logo and Hospital Name Section - Left */}
              <motion.div 
                className="flex items-center space-x-3 flex-shrink-0"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                {branding?.logoUrl ? (
                  <img 
                    src={branding.logoUrl} 
                    alt={`${hospitalName} Logo`} 
                    className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'block';
                    }}
                  />
                ) : (
                  <>
                    <Stethoscope className="h-8 w-8 text-blue-400" />
                    <span className="text-lg sm:text-xl md:text-2xl font-bold gradient-text hidden sm:inline">
                      {hospitalName}
                    </span>
                  </>
                )}
              </motion.div>
              
              {/* Contact Info & Social Media Section - Right, Same Row on Mobile */}
              <motion.div 
                className="flex items-center gap-2 sm:gap-3 lg:gap-4 text-xs sm:text-sm flex-shrink-0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {/* Phone */}
                {branding?.phone && (
                  <a 
                    href={`tel:${branding.phone}`} 
                    className="flex items-center gap-1 hover:text-blue-400 transition-colors text-gray-300"
                  >
                    <Phone className="h-4 w-4 text-blue-400 flex-shrink-0" />
                    <span className="whitespace-nowrap hidden sm:inline">{branding.phone}</span>
                    <span className="whitespace-nowrap sm:hidden text-xs">{branding.phone}</span>
                  </a>
                )}
                
                {/* Email */}
                {branding?.email && (
                  <a 
                    href={`mailto:${branding.email}`} 
                    className="flex items-center gap-1 hover:text-blue-400 transition-colors text-gray-300"
                  >
                    <Mail className="h-4 w-4 text-blue-400 flex-shrink-0" />
                    <span className="whitespace-nowrap hidden lg:inline">{branding.email}</span>
                    <span className="whitespace-nowrap hidden sm:inline lg:hidden">Email</span>
                  </a>
                )}
              </motion.div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold mb-6">
                <span className="gradient-text">{hospitalName}</span>
                <br />
                <span className="text-3xl md:text-4xl font-normal text-gray-300">Advanced Dental Practice with Integrated EHR Solution</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-4 max-w-3xl mx-auto">
                Delivering Exceptional Smiles
              </p>
              <p className="text-lg md:text-xl text-gray-400 mb-8 max-w-4xl mx-auto">
                At {hospitalName}, your oral health and comfort are our highest priority. Our multi-specialty dental clinic now offers a state-of-the-art Dental EHR & Practice Management System designed to elevate both patient experience and clinical outcomes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link to={`/${hospitalSlug}/login`}>
                  <Button size="lg" className="medical-gradient text-white hover:opacity-90 px-8 py-4 text-lg">
                    Doctor Login
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to={`/${hospitalSlug}/patient/login`}>
                  <Button size="lg" className="patient-gradient text-white hover:opacity-90 px-8 py-4 text-lg">
                    Patient Login
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              {/* Trial Period Banner - Only for TRIAL plan type */}
              {planType === 'TRIAL' && trialInfo && trialInfo.isActive && trialInfo.daysLeft > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="max-w-2xl mx-auto mb-8"
                >
                  <Card className="glass-effect border-yellow-500/50 bg-gradient-to-r from-yellow-500/10 to-orange-500/10">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <AlertCircle className="h-6 w-6 text-yellow-400 mt-1 flex-shrink-0" />
                        <div className="flex-1">
                        
                          <p className="text-gray-300 mb-3">
                            Your trial period ends on <strong className="text-white">{trialInfo.endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
                          </p>
                          <p className="text-yellow-400 font-semibold mb-4">
                            {trialInfo.daysLeft} {trialInfo.daysLeft === 1 ? 'day' : 'days'} left
                          </p>
                          <p className="text-gray-300">
                            Clinic admin can login and enroll before your trial period ends to continue using our services.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Monthly/Yearly Active Banner - Only for SILVER/GOLD plan type, last 7 days before renewal */}
              {(planType === 'SILVER' || planType === 'GOLD') && subscriptionInfo && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="max-w-2xl mx-auto mb-8"
                >
                  <Card className="glass-effect border-blue-500/50 bg-gradient-to-r from-blue-500/10 to-purple-500/10">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Clock className="h-6 w-6 text-blue-400 mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-white mb-2">
                            {subscriptionInfo.planType === 'SILVER' ? 'Silver' : 'Gold'} Plan Active
                          </h3>
                          <p className="text-gray-300 mb-3">
                            Your {subscriptionInfo.currentPlan === 'MONTHLY' ? 'Monthly' : 'Yearly'} subscription renews on <strong className="text-white">{subscriptionInfo.renewalDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
                          </p>
                          <p className="text-blue-400 font-semibold mb-4">
                            {subscriptionInfo.daysUntilRenewal === 0 ? 'Renews today' : 
                             subscriptionInfo.daysUntilRenewal === 1 ? '1 day until renewal' : 
                             `${subscriptionInfo.daysUntilRenewal} days until renewal`}
                          </p>
                          <p className="text-gray-300 mb-4">
                            Your subscription will automatically renew. No action needed.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
              
             
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold pb-4 gradient-text">
                Transforming Dental Care with Intelligent EHR & Practice Management
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Our comprehensive Dental Electronic Health Record (EHR) system empowers {hospitalName} clinicians and staff to deliver more efficient, coordinated, and personalized oral healthcare.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="glass-effect border-gray-700 card-hover h-full">
                    <CardHeader>
                      <feature.icon className="h-12 w-12 text-blue-400 mb-4" />
                      <CardTitle className="text-white">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-300">
                        {feature.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
                Why {hospitalName}?
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                We deliver modern dental solutions with patient-centric care and advanced technology. Our integrated EHR platform supports enhanced clinical accuracy, faster workflows, and more meaningful dentist-patient communication.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card className="glass-effect border-gray-700 card-hover h-full">
                  <CardHeader>
                    <Smile className="h-12 w-12 text-blue-400 mb-4" />
                    <CardTitle className="text-white">Comprehensive Dentistry Services</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-300">
                      Endodontics, Orthodontics, Prosthodontics, Oral Surgery, Laser Dentistry, Implants, and more.
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card className="glass-effect border-gray-700 card-hover h-full">
                  <CardHeader>
                    <MapPin className="h-12 w-12 text-blue-400 mb-4" />
                    <CardTitle className="text-white">Conveniently Located</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-300">
                      {branding?.address || 'Easy access for all your dental care needs.'}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <motion.div
              className="glass-effect rounded-2xl p-12 border-gray-700"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 gradient-text text-center">
                Why Patients Love About Us
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  'Highly experienced and friendly dental specialists',
                  'Precise diagnosis with modern tools',
                  'Comfortable, caring treatment experience',
                  'Easy appointment management and follow-up care',
                  'Now backed by intelligent EHR technology for smoother visits and better outcomes',
                  'All designed to help you smile brighter and healthier'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
                    <p className="text-gray-300">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6 gradient-text">
                Get Started with {hospitalName} EHR Today
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Experience a smarter way to manage your dental care and clinic operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link to={`/${hospitalSlug}/login`}>
                  <Button size="lg" className="medical-gradient text-white hover:opacity-90 px-8 py-4 text-lg">
                    Doctor Login
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to={`/${hospitalSlug}/patient/login`}>
                  <Button size="lg" className="patient-gradient text-white hover:opacity-90 px-8 py-4 text-lg">
                    Patient Login
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              
              {/* Contact Information Section */}
              {(branding?.phone || branding?.email || branding?.address) && (
                <div className="mt-12 space-y-6">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold mb-6 gradient-text">Contact</h3>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-gray-300 mb-8">
                      {branding?.phone && (
                        <a 
                          href={`tel:${branding.phone}`} 
                          className="flex items-center gap-2 hover:text-blue-400 transition-colors"
                        >
                          <Phone className="h-5 w-5 text-blue-400" />
                          <span>{branding.phone}</span>
                        </a>
                      )}
                      {branding?.email && (
                        <a 
                          href={`mailto:${branding.email}`} 
                          className="flex items-center gap-2 hover:text-blue-400 transition-colors"
                        >
                          <Mail className="h-5 w-5 text-blue-400" />
                          <span>{branding.email}</span>
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Address */}
                  {branding?.address && (
                    <div className="rounded-lg p-6 border border-gray-700 bg-gradient-to-br from-blue-600/20 via-cyan-600/20 to-teal-600/20 hover:from-purple-600/40 hover:via-pink-600/40 hover:to-rose-600/40 transition-all duration-300 backdrop-blur-sm">
                      <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-blue-400" />
                        Location
                      </h4>
                      <p className="leading-relaxed text-gray-300">
                        {branding.address}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-gray-700 py-12 px-4 sm:px-6 lg:px-8 mt-12">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              {branding?.logoUrl ? (
                <img 
                  src={branding.logoUrl} 
                  alt={`${hospitalName} Logo`} 
                  className="h-8 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.nextElementSibling;
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
              ) : (
                <>
                  <Stethoscope className="h-6 w-6 text-blue-400" />
                  <span className="text-lg font-semibold gradient-text">{hospitalName}</span>
                </>
              )}
            </div>
            <p className="text-gray-400 text-sm">
              ©2026 Dental Hub, Product of  <a 
                href="https://www.neinus.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                Neinus Technologies
              </a>. All rights reserved.
              
            </p>
          </div>
        </footer>

        {/* WhatsApp Chat & Call Buttons */}
        {/* WhatsApp message uses hospital phone (or defaults to USA), Call uses India number */}
        {branding?.phone && (
          <WhatsAppButton 
            phoneNumber={branding.phone} 
            callNumber={branding.phone} 
            message={`Hello, I'd like to know more about ${hospitalName} services.`}
          />
        )}
      </div>
    </>
  );
};

export default HospitalLandingPage;

